import { Box, Divider, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import InfoIcon from "../../../../../../../assets/images/icons/InfoIcon";
import BriaTooltip from "../../../../../../../components/common/BriaTooltip/BriaTooltip";
import FileUploader from "../../../../../../../components/common/FileUploader/FileUploader";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { LogoTypeMappingEnum } from "../../../../../../../models/common";
import { isAdsEditor } from "../../../../../../../utils";
import useDesignEditorUtils from "../../../../../../CustomUtils/UseDesignEditor";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import UseAdsEditorUtils, { logoConfigLabel } from "../../../../../../hooks/useAdsEditorUtils";
import { useEditor } from "../../../../../../hooks/useEditor";
import { LayerType } from "../../../../../../types";
import common from "../Brands/Common.module.scss";
import { toBase64 } from "../Uploads";
import styles from "./Logos.module.scss";

const Logos = () => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.logos" });
	const { brandsDefinitionStore, campaignStore } = useAppStore();
	const logoKeys = Object.keys(LogoTypeMappingEnum) as Array<keyof typeof LogoTypeMappingEnum>;
	const { addImageObjectToCanvas } = useDesignEditorUtils();
	const { getFirstAvailableConfig, getFabricLayerTakenConfigs } = UseAdsEditorUtils();
	const editor = useEditor();
	const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
	const hasLogos = () => {
		return brandsDefinitionStore.globalSelectedBrand.logos?.some((logo) => logo.src && logo.src !== "");
	};
	const handleUploadLogoAndadditToCanvas = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setIsImageLoading(true);
		const _currentScene = editor?.scene.exportToJSON();
		if (_currentScene) {
			if (!isAdsEditor()) {
				const usedConfigs = getFabricLayerTakenConfigs(_currentScene?.layers, LayerType.STATIC_IMAGE, true);
				const firstAvailableConfig = getFirstAvailableConfig(
					usedConfigs,
					LayerType.STATIC_IMAGE,
					logoConfigLabel,
				);
				if (!firstAvailableConfig) {
					campaignStore.setProperty("nonAvailableConfigSnackbarOpen", true);
					return;
				}
			}
			const file = e.target.files?.[0];
			if (!file) return;

			const newLogoSrc = (await toBase64(file)) as string;
			if (newLogoSrc) {
				const newLogoToAdd = {
					id: "",
					visual_hash: "",
					url: newLogoSrc,
					isLogo: true,
				};
				addImageObjectToCanvas(newLogoToAdd);
			}
			setIsImageLoading(false);
		}
	};

	return (
		<Box className={styles.logosTabContainer}>
			<MainTabHeader title={t("title")} />
			<Box className={styles.tabs}>
				<Box className={styles.fullWidth}>
					<Box className={styles.uploadLogoWrapper}>
						<Box className={styles.uploadLogoTextIconWrapper}>
							<Typography className={styles.uploadLogoText}>{t("uploadLogo")}</Typography>
							<BriaTooltip
								title={t("toolTip")}
								className={styles.info}
								componentsProps={{ tooltip: { className: styles.tooltip } }}
								displayOn="hover"
							>
								<InfoIcon className={styles.infoIcon} />
							</BriaTooltip>
						</Box>
						<FileUploader
							buttonText={"Upload"}
							buttonType="primaryMedium"
							inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp" }}
							onUpload={handleUploadLogoAndadditToCanvas}
							buttonClassName={clsx(styles.primaryButton, {
								[styles.disabled]: false,
							})}
							loading={isImageLoading}
							disabled={isImageLoading}
						/>
					</Box>

					{hasLogos() && (
						<>
							<Divider className={styles.divider} />{" "}
							<Typography className={styles.brandLogosLabel}>{t("brandLogos")}</Typography>{" "}
						</>
					)}
					{brandsDefinitionStore.globalSelectedBrand?.logos
						?.map((logo, index) => ({ logo, key: logoKeys[index] }))
						.filter(({ logo }) => logo?.src.trim().length !== 0)
						.map(({ logo, key }, index) => (
							<Box
								onClick={() => {
									addImageObjectToCanvas({
										id: "",
										visual_hash: "",
										url: logo.src,
										isLogo: true,
										logoType: logo.type,
									});
								}}
								className={common.listItem}
							>
								<Box key={index} className={common.itemContent}>
									<Box
										className={clsx(common.itemPreview, {
											[common.emptyStatus]: false,
											[styles.logoItemPreviewBackGround]: true,
										})}
									>
										<img src={logo.src} alt={`${logo?.name}`} />
									</Box>
									<Typography className={common.itemLabel}>{LogoTypeMappingEnum[key]}</Typography>
								</Box>
							</Box>
						))}
				</Box>
			</Box>
		</Box>
	);
};
const ObservedComponent = observer(Logos);
export default ObservedComponent;
